import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { KeycloakAuthService } from '../services/keycloak-auth.service';
import {
  Group,
  ClientRole,
  AddUserPayload,
  AddUserResponse,
} from '../models/userManagement';
import { timer } from 'rxjs';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
})
export class UserManagementComponent implements OnInit {
  private fb = inject(FormBuilder);
  private auth = inject(KeycloakAuthService);
  private platformId = inject(PLATFORM_ID);

  addUserForm!: FormGroup;
  groups: Group[] = [];
  roles: ClientRole[] = [];
  isLoading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  errorDetails: string | null = null; 

 ngOnInit(): void {
    this.buildForm();
    if (isPlatformBrowser(this.platformId)) {
      this.loadGroups();
      this.loadClientRoles();
    }
  }

  private buildForm(): void {
    this.addUserForm = this.fb.group({
      username: ['', Validators.required, Validators.minLength(5)],
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
      emailVerified: [true],
      groupName: ['', Validators.required],
      roleNames: [[], Validators.required],
    });
  }

  private loadGroups(): void {
    this.auth.getGroups().subscribe({
      next: (g) => (this.groups = g),
      error: () => this.showError('Failed to load groups'),
    });
  }

  private loadClientRoles(): void {
    this.auth.getClientRoles().subscribe({
      next: (r) => (this.roles = r),
      error: () => this.showError('Failed to load client roles'),
    });
  }

  /** ---------- SUBMIT ---------- */
  onSubmit(): void {
    if (this.addUserForm.invalid) return;

    this.clearMessages();
    this.isLoading = true;

    const payload: AddUserPayload = {
      ...this.addUserForm.value,
      // make sure booleans stay booleans
      emailVerified: !!this.addUserForm.value.emailVerified,
    };

    this.auth.addUser(payload).subscribe({
      next: (res: AddUserResponse) => {
        this.showSuccess(`${res.message} (ID: ${res.userId})`);
        // reset form but keep the defaults
        this.addUserForm.reset({
          emailVerified: false,
          groupName: null,
          roleNames: [],
        });
      },
      error: (err: HttpErrorResponse) => {
        // Backend now returns { error: "...", details: "..." }
        const backendMsg = err.error?.error ?? 'Failed to add user';
        const details = err.error?.details ?? err.message;
        this.showError(backendMsg, details);
      },
    }).add(() => (this.isLoading = false));
  }

  /** ---------- MESSAGE HELPERS ---------- */
  private showSuccess(msg: string): void {
    this.successMessage = msg;
    this.autoClear();
  }

  private showError(msg: string, details?: string): void {
    this.errorMessage = msg;
    this.errorDetails = details ?? null;
    this.autoClear();
  }

  private clearMessages(): void {
    this.successMessage = null;
    this.errorMessage = null;
    this.errorDetails = null;
  }

  /** Auto-hide after 5 seconds (optional) */
  private autoClear(): void {
    timer(5000).subscribe(() => this.clearMessages());
  }
}